import type { Ordres } from "../domaine/analyses";
import type { Selection } from "../domaine/facettes";
import { classesColorees, emplacementCouleur } from "../domaine/graphe";
import type { Similarites } from "../domaine/similarite";
import type { DateIso, Portefeuille, Projet } from "../domaine/types";

/**
 * Les vues, dans l'ordre des onglets. `seuil` dit si le curseur de rapprochement a
 * un sens ici : une matrice montre toutes les valeurs, elle n'a rien à seuiller.
 * Paramètres et Avis sont à droite, à part : ce ne sont pas des vues de la donnée.
 * L'accueil n'a pas d'onglet : on y revient par l'enseigne.
 */
export const VUES = [
  { id: "accueil", libelle: "Accueil", icone: "reseau", seuil: false, aDroite: false },
  { id: "graphe", libelle: "Réseau des projets", icone: "reseau", seuil: true, aDroite: false },
  { id: "arbres", libelle: "Cultures et filières", icone: "arbre", seuil: false, aDroite: false },
  { id: "leviers", libelle: "Leviers par projet", icone: "grille", seuil: false, aDroite: false },
  { id: "autres", libelle: "Autres analyses", icone: "graphiques", seuil: false, aDroite: false },
  { id: "parametres", libelle: "Paramètres", icone: "reglages", seuil: false, aDroite: true },
  { id: "avis", libelle: "Votre avis", icone: "bulle", seuil: false, aDroite: true },
] as const;
export type IdVue = (typeof VUES)[number]["id"];

export const estVue = (v: string | null): v is IdVue => VUES.some((x) => x.id === v);

/** Vues cachées : aucun onglet, sauf celui de la vue ouverte. */
const CACHEES: readonly IdVue[] = ["accueil"];

/** Les vues qui ont un onglet, dans l'ordre, pour la vue ouverte. */
export const ongletsPour = (ouverte: IdVue) =>
  VUES.filter((v) => !CACHEES.includes(v.id) || (v.id === ouverte && v.id !== "accueil"));

/**
 * Vue à ouvrir d'après l'adresse. Sans vue, l'accueil ; mais un lien vers un projet
 * ouvre le graphe, où sa fiche a un sens. Les anciennes vues, regroupées depuis dans
 * « Autres graphiques », rouvrent au bon endroit ; l'ancien onglet Infos, à l'accueil.
 * L'ancien explorateur rouvre le graphe : ses filtres valent désormais partout.
 */
export function vueDepuisAdresse(v: string | null, projet: string | null = null): IdVue {
  if (v === "explorateur") return "graphe";
  // L'onglet Lacunes (0.3.0) a laissé place à une figure des Autres analyses.
  if (v === "lacunes") return "autres";
  if (v === "similarite" || v === "croisements" || v === "exploration") return "autres";
  if (estVue(v)) return v;
  return projet ? "graphe" : "accueil";
}

/** Accueil, Paramètres et Avis ne montrent pas la donnée : ni barre, ni légende, ni panneau. */
export const surDonnee = (v: IdVue): boolean =>
  v !== "accueil" && v !== "parametres" && v !== "avis";

/**
 * Ce que toutes les vues d'analyse partagent. Construit par Atlas avec des
 * accesseurs : les vues restent réactives sans recevoir dix props chacune.
 */
export interface Analyse {
  /** Le portefeuille après le filtre d'avancement des Paramètres et les filtres de la barre. */
  readonly portefeuille: Portefeuille;
  readonly similarites: Similarites;
  readonly ordres: Ordres;
  readonly aujourdhui: DateIso;
  /** Référence du projet sélectionné. */
  readonly selection: string | null;
  /** Texte tapé dans la recherche, brut. */
  readonly recherche: string;
  /** Indices des projets trouvés par la recherche ; null sans recherche. */
  readonly misEnAvant: ReadonlySet<number> | null;
  selectionner(ref: string | null): void;
  allerA(vue: IdVue): void;
  /** Remplace les critères des facettes données, garde les autres. */
  filtrerSur(s: Selection): void;
}

/** Teintes des classes visées par un projet, vide au-delà de trois classes (ADR-0005 R4 du POC). */
export function couleursCibles(portefeuille: Portefeuille, projet: Projet): string[] {
  const colorees = classesColorees(portefeuille.classesBioagresseur);
  if (!colorees.length) return [];
  return projet.bioagresseurs.map((c) => {
    const k = emplacementCouleur(c, colorees);
    return k === null ? "var(--cible-autre)" : `var(--cible-${k})`;
  });
}

/**
 * Fond d'une pastille : la teinte seule, ou un disque partagé en secteurs égaux
 * quand il y en a plusieurs — comme les nœuds du graphe et la légende.
 */
export function fondPastille(couleurs: readonly string[]): string {
  if (couleurs.length <= 1) return couleurs[0] ?? "var(--cible-autre)";
  const n = couleurs.length;
  const parts = couleurs.map((c, i) => `${c} ${(i * 100) / n}% ${((i + 1) * 100) / n}%`);
  return `conic-gradient(from -90deg, ${parts.join(", ")})`;
}

/** Teinte d'une classe de bioagresseur, par position dans le référentiel. */
export function couleurClasse(portefeuille: Portefeuille, classe: string): string {
  const k = emplacementCouleur(classe, classesColorees(portefeuille.classesBioagresseur));
  return k === null ? "var(--cible-autre)" : `var(--cible-${k})`;
}
